import type { Location } from './types';

/** Adresvelden van een locatie (zoals public.save_location ze verwacht, migratie 0012). */
export interface AddressInput {
  street: string;
  house_number: string;
  house_number_addition: string;
  postal_code: string;
  city: string;
  /** ISO-landcode; standaard NL. */
  country: string;
}

export const EMPTY_ADDRESS: AddressInput = { street: '', house_number: '', house_number_addition: '', postal_code: '', city: '', country: 'NL' };

/** '1234ab', ' 1234  AB ' → '1234 AB'; ongeldig → null. Zelfde regel als app.normalize_nl_postcode. */
export function normalizePostcode(input: string): string | null {
  const v = input.replace(/\s/g, '').toUpperCase();
  return /^[1-9][0-9]{3}[A-Z]{2}$/.test(v) ? `${v.slice(0, 4)} ${v.slice(4)}` : null;
}

/** Huisnummer met toevoeging: één letter direct erachter ("12a"), anders met een spatie ("12 bis"). */
export function houseNumberLabel(nr: string | null | undefined, addition: string | null | undefined): string {
  const n = (nr ?? '').trim();
  const a = (addition ?? '').trim();
  if (!a) return n;
  return /^[A-Za-z]$/.test(a) ? `${n}${a.toLowerCase()}` : `${n} ${a}`;
}

/** "Sniepweg 12a, 1112 AB Diemen". Zelfde opmaak als app.format_address; valt terug op het vrije adres. */
export function formatAddress(
  loc: Pick<Location, 'address'> & Partial<Pick<Location, 'street' | 'house_number' | 'house_number_addition' | 'postal_code' | 'city'>>,
): string {
  if (loc.street) {
    const line1 = [loc.street.trim(), houseNumberLabel(loc.house_number, loc.house_number_addition)].filter(Boolean).join(' ');
    const line2 = [loc.postal_code?.trim(), loc.city?.trim()].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ');
  }
  return loc.address?.trim() ?? '';
}

export function addressFromLocation(loc: Location | null | undefined): AddressInput {
  if (!loc) return { ...EMPTY_ADDRESS };
  return {
    street: loc.street ?? '',
    house_number: loc.house_number ?? '',
    house_number_addition: loc.house_number_addition ?? '',
    postal_code: loc.postal_code ?? '',
    city: loc.city ?? '',
    country: loc.country ?? 'NL',
  };
}

export type AddressErrors = Partial<Record<keyof AddressInput, string>>;

/**
 * Controleert een adres. required = alle vier de velden verplicht (club aanmaken, locatie in het portaal);
 * anders mag het adres leeg blijven, maar niet half ingevuld.
 */
export function validateAddress(a: AddressInput, opts: { required?: boolean } = {}): AddressErrors {
  const errs: AddressErrors = {};
  const any = [a.street, a.house_number, a.house_number_addition, a.postal_code, a.city].some((v) => v.trim() !== '');
  if (!any && !opts.required) return errs;
  if (!a.street.trim()) errs.street = 'Vul de straat in.';
  if (!a.house_number.trim()) errs.house_number = 'Vul het huisnummer in.';
  else if (!/^[1-9][0-9]{0,4}$/.test(a.house_number.trim())) errs.house_number = 'Alleen cijfers; een letter gaat in het vak toevoeging.';
  if (a.house_number_addition.trim().length > 10) errs.house_number_addition = 'Maximaal 10 tekens.';
  if (!a.postal_code.trim()) errs.postal_code = 'Vul de postcode in.';
  else if ((a.country || 'NL') === 'NL' && !normalizePostcode(a.postal_code)) errs.postal_code = 'Een postcode is 4 cijfers en 2 letters, bijvoorbeeld 1234 AB.';
  if (!a.city.trim()) errs.city = 'Vul de plaats in.';
  return errs;
}

/** Genormaliseerde waarden voor de database (lege velden → null, postcode als "1234 AB"). */
export function cleanAddress(a: AddressInput) {
  const country = (a.country || 'NL').trim().toUpperCase();
  const pc = a.postal_code.trim();
  return {
    street: a.street.trim() || null,
    house_number: a.house_number.trim() || null,
    house_number_addition: a.house_number_addition.trim() || null,
    postal_code: pc ? (country === 'NL' ? normalizePostcode(pc) ?? pc : pc.toUpperCase()) : null,
    city: a.city.trim() || null,
    country,
  };
}
